import { FunctionComponent, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components";
import { ListOwnership } from "@/enum/list.enum";
import { useGetCampaignsOverviewQuery } from "@/pages/Private/redux/list/campaigns-overview.api";
import { useSnackBar } from "@/utils/useSnackbar";

import { useCampaignsFilters } from "./hooks/useCampaignsFilters";
import { CampaignPinnedFilterTab } from "./utils/campaignPinnedFilterTabs";
import { CampaignsOwnershipTabs } from "./components/CampaignsOwnershipTabs";
import { CampaignsStatStrip } from "./components/CampaignsStatStrip";
import { CampaignsTable } from "./components/CampaignsTable";

/**
 * Campaigns overview (PREC-3880): every campaign across all lead lists in one place,
 * mirroring the List overview's grouped table (lists as parent rows, campaigns as sub-rows).
 * The stat strip and tab bubbles come from the batched `/list/campaigns-overview` aggregates.
 *
 * Filter state lives in `useCampaignsFilters` at this level rather than inside the table, because
 * a pinned tab restores ownership *and* the filters together — and the tab strip that renders the
 * pinned chips is here while the filter bar is down in the table.
 */
export const Campaigns: FunctionComponent = () => {
	const { t } = useTranslation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	// Set by the table so a restored pin can reset paging without lifting pagination too.
	const resetPagingRef = useRef<(() => void) | null>(null);
	const handleFiltersRestored = useCallback(() => resetPagingRef.current?.(), []);

	const filters = useCampaignsFilters({ onFiltersRestored: handleFiltersRestored });

	const { data: stats } = useGetCampaignsOverviewQuery({
		ownership: filters.ownership === ListOwnership.ALL ? undefined : filters.ownership,
	});

	const toastError = () => handleOpenSnackbar(t("list.pinTab.errorToast"));

	const handlePinCurrentView = (name: string) => {
		// The trigger is disabled in this state; guard anyway so no future caller can create a twin.
		if (!filters.canPin) {
			return;
		}

		filters
			.addPinnedTab(name, filters.currentFilters)
			.then(() => handleOpenSnackbar(t("list.pinTab.pinnedToast", { name })))
			.catch(toastError);
	};

	const handleUpdatePinnedTab = (id: number) => {
		const tab = filters.pinnedTabs.find((pinned) => pinned.id === id);

		if (!tab || filters.isPinnedTabUpToDate(tab)) {
			return;
		}

		filters
			.updatePinnedTabFilters(id, filters.currentFilters)
			.then(() => handleOpenSnackbar(t("list.pinTab.updatedToast", { name: tab.name })))
			.catch(toastError);
	};

	const handleDeletePinnedTab = (id: number) => {
		const tab = filters.pinnedTabs.find((pinned) => pinned.id === id);

		filters
			.removePinnedTab(id)
			.then(() => {
				if (tab) {
					handleOpenSnackbar(t("list.pinTab.deletedToast", { name: tab.name }));
				}
			})
			.catch(toastError);
	};

	const handleRenamePinnedTab = (id: number, name: string) => {
		filters.renamePinnedTab(id, name).catch(toastError);
	};

	const handleSelectPinnedTab = (tab: CampaignPinnedFilterTab) =>
		filters.applyPinnedFilters(tab.filters);

	return (
		<div className="flex flex-col h-full w-full">
			<PageHeader title={t("sidebar.campaigns")}>
				<CampaignsOwnershipTabs
					activePinnedTabId={filters.activePinnedTabId}
					counts={{
						[ListOwnership.MINE]: stats?.campaignCounts.mine,
						[ListOwnership.TEAM]: stats?.campaignCounts.team,
						[ListOwnership.ALL]: stats?.campaignCounts.all,
					}}
					pinnedTabs={filters.pinnedTabs}
					value={filters.ownership}
					onChange={filters.setOwnership}
					onDeletePinnedTab={handleDeletePinnedTab}
					onRenamePinnedTab={handleRenamePinnedTab}
					onSelectPinnedTab={handleSelectPinnedTab}
					onUpdatePinnedTab={handleUpdatePinnedTab}
				/>
			</PageHeader>
			<CampaignsStatStrip stats={stats} />
			<CampaignsTable
				filters={filters}
				registerResetPaging={(reset) => {
					resetPagingRef.current = reset;
				}}
				onPinCurrentView={handlePinCurrentView}
			/>
			{renderMessageSnackbar()}
		</div>
	);
};
